import {
  IconAddressBook,
  IconAffiliate,
  IconBell,
  IconCalendar,
  IconChevronLeft,
  IconChevronRight,
  IconCode,
  IconCreditCard,
  IconFilePencil,
  IconFileText,
  IconFolder,
  IconForms,
  IconGridDots,
  IconHeadset,
  IconLock,
  IconMenu2,
  IconMessageQuestion,
  IconMessageReply,
  IconPackage,
  IconPlugConnected,
  IconRocket,
  IconUserCog,
  IconUsersGroup,
  TablerIcon,
} from "@tabler/icons-react";
import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { __ } from "@common/helpers/i18n";
import { CounterBadge } from "@app/elements/text";
import { checkIfActive } from "@app/helpers/menu";
import { useDocumentDirection } from "@app/hooks";
import type { ChildMenu } from "@app/layout/Menu";

export type SubMenuItemsProps = {
  items: ChildMenu[];
  baseUrl?: string;
  showSelectOnSmall?: boolean;
};

const subMenuIcons: Record<string, TablerIcon> = {
  "/posts": IconFileText,
  "/schedule-calendar": IconCalendar,
  "/draft": IconFilePencil,
  "/accounts": IconPlugConnected,
  "/team": IconUsersGroup,
  "/integrations": IconGridDots,
  "/ticket": IconHeadset,
  "/user/profile": IconUserCog,
  "/user/security": IconLock,
  "/clients": IconCode,
  "/user/notifications": IconBell,
  "/renew": IconRocket,
  "/order": IconCreditCard,
  "/settings/affiliate": IconAffiliate,
  "/response": IconMessageReply,
  "/form": IconForms,
  "/menu": IconMenu2,
  "/question": IconMessageQuestion,
  "/product": IconPackage,
  "/leed": IconAddressBook,
};

export default function SubMenuItems({
  items,
  baseUrl = "",
}: SubMenuItemsProps) {
  const location = useLocation();
  const direction = useDocumentDirection();
  const mobileListRef = useRef<HTMLDivElement | null>(null);
  const [mobileScrollState, setMobileScrollState] = useState({
    canScrollEnd: false,
    canScrollStart: false,
  });

  const activeChild = items.find((child) =>
    checkIfActive(location, [baseUrl + child.url], child.exact),
  );
  const isRtl = direction === "rtl";
  const StartIcon = isRtl ? IconChevronRight : IconChevronLeft;
  const EndIcon = isRtl ? IconChevronLeft : IconChevronRight;

  const updateMobileScrollState = useCallback(() => {
    const list = mobileListRef.current;
    const cards = list
      ? Array.from(
          list.querySelectorAll<HTMLElement>("[data-submenu-card='true']"),
        )
      : [];

    if (!list || cards.length === 0) {
      setMobileScrollState({
        canScrollEnd: false,
        canScrollStart: false,
      });
      return;
    }

    const containerRect = list.getBoundingClientRect();
    const firstRect = cards[0].getBoundingClientRect();
    const lastRect = cards[cards.length - 1].getBoundingClientRect();
    const offset = 2;
    const canScrollStart = isRtl
      ? firstRect.right > containerRect.right + offset
      : firstRect.left < containerRect.left - offset;
    const canScrollEnd = isRtl
      ? lastRect.left < containerRect.left - offset
      : lastRect.right > containerRect.right + offset;

    setMobileScrollState((current) =>
      current.canScrollStart === canScrollStart &&
      current.canScrollEnd === canScrollEnd
        ? current
        : { canScrollEnd, canScrollStart },
    );
  }, [isRtl]);

  useEffect(() => {
    const list = mobileListRef.current;
    if (!list) return;

    const frame = window.requestAnimationFrame(updateMobileScrollState);
    list.addEventListener("scroll", updateMobileScrollState, { passive: true });
    window.addEventListener("resize", updateMobileScrollState);

    return () => {
      window.cancelAnimationFrame(frame);
      list.removeEventListener("scroll", updateMobileScrollState);
      window.removeEventListener("resize", updateMobileScrollState);
    };
  }, [items.length, updateMobileScrollState]);

  useEffect(() => {
    const list = mobileListRef.current;
    if (!list || !activeChild) return;

    let timeout: number | undefined;
    const frame = window.requestAnimationFrame(() => {
      const cards = Array.from(
        list.querySelectorAll<HTMLElement>("[data-submenu-url]"),
      );
      const activeIndex = cards.findIndex(
        (card) => card.dataset.submenuUrl === activeChild.url,
      );
      const activeCard = cards[activeIndex];
      const shouldCenter = activeIndex > 0 && activeIndex < cards.length - 1;

      activeCard?.scrollIntoView({
        block: "nearest",
        inline: shouldCenter ? "center" : "nearest",
      });

      timeout = window.setTimeout(updateMobileScrollState, 80);
    });

    return () => {
      window.cancelAnimationFrame(frame);
      if (timeout) window.clearTimeout(timeout);
    };
  }, [activeChild, updateMobileScrollState]);

  const scrollMobileItems = (scrollDirection: "start" | "end") => {
    mobileListRef.current?.scrollBy({
      behavior: "smooth",
      left:
        scrollDirection === "end" ? (isRtl ? -180 : 180) : isRtl ? 180 : -180,
    });
  };

  return (
    <div className="px-0 md:p-0">
      <div className="relative md:hidden">
        <div
          ref={mobileListRef}
          className="flex min-w-0 snap-x snap-mandatory gap-2 overflow-x-auto px-0.5 py-1 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {items.map((child, index) => {
            const url = baseUrl + child.url;
            const isActive = activeChild === child;
            const Icon = subMenuIcons[child.url] || IconFolder;

            return (
              <Link
                key={index}
                to={!child.external ? url : { pathname: child.url }}
                target={child.external ? "_blank" : undefined}
                data-submenu-card="true"
                data-submenu-url={child.url}
                className={classNames(
                  "flex h-[76px] w-[9rem] flex-shrink-0 snap-center flex-col justify-between rounded-md border bg-card p-2.5 text-start shadow-soft transition-all duration-150",
                  isActive
                    ? "border-primary/35 text-primary ring-1 ring-primary/10"
                    : "border-border text-foreground hover:border-foreground/20 hover:bg-muted/30",
                )}
                aria-current={isActive ? "page" : undefined}
              >
                <span className="flex items-center justify-between gap-2">
                  <span
                    className={classNames(
                      "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    <Icon size={18} stroke={2} />
                  </span>
                  {isActive && (
                    <span className="h-2 w-2 rounded-full bg-primary" />
                  )}
                </span>
                <span className="block line-clamp-2 text-sm font-extrabold leading-5">
                  {__(child.label as any)}
                </span>
              </Link>
            );
          })}
        </div>
        {items.length > 2 && (
          <>
            {mobileScrollState.canScrollStart && (
              <div
                className={classNames(
                  "pointer-events-none absolute inset-y-0 start-0 w-16",
                  isRtl
                    ? "bg-gradient-to-l from-background via-background/95 to-transparent"
                    : "bg-gradient-to-r from-background via-background/95 to-transparent",
                )}
              />
            )}
            {mobileScrollState.canScrollEnd && (
              <div
                className={classNames(
                  "pointer-events-none absolute inset-y-0 end-0 w-16",
                  isRtl
                    ? "bg-gradient-to-r from-background via-background/95 to-transparent"
                    : "bg-gradient-to-l from-background via-background/95 to-transparent",
                )}
              />
            )}
            <button
              type="button"
              aria-label={__("Previous")}
              onClick={() => scrollMobileItems("start")}
              className={classNames(
                "absolute start-1 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-card/55 text-muted-foreground/55 ring-1 ring-border/50 transition-all hover:bg-card hover:text-foreground",
                !mobileScrollState.canScrollStart &&
                  "pointer-events-none opacity-0",
              )}
            >
              <StartIcon size={16} stroke={2} />
            </button>
            <button
              type="button"
              aria-label={__("Next")}
              onClick={() => scrollMobileItems("end")}
              className={classNames(
                "absolute end-1 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-card/55 text-muted-foreground/55 ring-1 ring-border/50 transition-all hover:bg-card hover:text-foreground",
                !mobileScrollState.canScrollEnd &&
                  "pointer-events-none opacity-0",
              )}
            >
              <EndIcon size={16} stroke={2} />
            </button>
          </>
        )}
      </div>
      <div className="relative hidden w-full min-w-0 md:block md:after:absolute md:after:inset-x-0 md:after:bottom-0 md:after:h-0.5 md:after:bg-border">
        <div className="flex w-full min-w-0 items-center gap-2 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] md:items-end md:gap-8 md:pb-0 [&::-webkit-scrollbar]:hidden">
          {items.map((child, index) => {
            const url = baseUrl + child.url;

            const isActive = activeChild === child;
            const Icon = subMenuIcons[child.url] || IconFolder;
            return (
              <Link
                key={index}
                to={!child.external ? url : { pathname: child.url }}
                target={child.external ? "_blank" : undefined}
                className={classNames(
                  isActive
                    ? "border-primary/25 bg-primary/10 text-primary md:border-transparent md:bg-transparent md:text-foreground"
                    : "border-border bg-card text-muted-foreground hover:border-foreground/20 hover:bg-muted/50 hover:text-foreground md:border-transparent md:bg-transparent md:hover:bg-transparent",
                  "group relative z-10 flex h-10 flex-shrink-0 items-center rounded-md border px-3 text-xs font-bold transition-all duration-150 ease-in-out md:h-12 md:rounded-none md:border-0 md:px-1 md:text-sm md:font-semibold",
                )}
                aria-current={isActive ? "page" : undefined}
              >
                <Icon
                  className={classNames(
                    isActive
                      ? "text-primary md:text-foreground"
                      : "text-muted-foreground group-hover:text-foreground",
                    "me-2 h-[17px] w-[17px] flex-shrink-0 opacity-80 md:group-hover:text-foreground",
                  )}
                  aria-hidden="true"
                  stroke={2}
                />
                <span className="truncate">{__(child.label as any)}</span>
                {child.unread && child.unread() !== undefined ? (
                  <CounterBadge
                    value={child.unread()}
                    max={99}
                    size="sm"
                    className={classNames(
                      "ms-auto",
                      !child.icon ? "bg-card" : "bg-muted text-foreground",
                    )}
                  />
                ) : null}
                {isActive && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 z-20 hidden h-0.5 rounded-full bg-primary md:block"
                  />
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
